import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  SharedInjectionMod,
  SharedInjectionModSchema,
} from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import {
  InjectionMod,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";

export const injectionModHandler: ModHandler<InjectionMod> = {
  normalize: (mod) => ({
    ...mod,
    js: mod.js.trim(),
    css: mod.css.trim(),
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteInjection") {
      throw new Error("Expected onsiteInjection");
    }

    const injection = conversion.dto;
    return {
      type: "injection",
      category: "basicMod",
      id: injection.id ?? "",
      variationId: injection.variationId,
      js: injection.customJs ?? "",
      css: injection.customCss ?? "",
      jsInjectionMode: injection.jsInjectionMode,
      tab: "css",
      source: injection.source ?? undefined,
    } as const;
  },

  convertFrom: (mod) => ({
    type: "onsiteInjection",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      jsInjectionMode: mod.jsInjectionMode,
      customCss: mod.css,
      customJs: mod.js,
      source: mod.source ?? null,
    },
  }),

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    if (!mod.js && !mod.css) {
      errors.js = "js or css is required";
      errors.css = "js or css is required";
    }
    return errors;
  },

  isEquivalent: (_a, b) => b.type === "injection",

  defaults: () => ({
    id: v4(),
    type: "injection",
    category: "basicMod",
    variationId: "",
    js: "",
    css: "",
    jsInjectionMode: { type: "immediately" },
    tab: "css",
  }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedInjectionMod>(SharedInjectionModSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? injectionModHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod: InjectionMod, newMod: InjectionMod) => {
    const normalize = (mod: InjectionMod) => ({
      description: mod.description,
      js: mod.js,
      jsInjectionMode: mod.jsInjectionMode,
      css: mod.css,
    });
    return !isEqual(normalize(oldMod), normalize(newMod));
  },
};
